import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, Divider, Grid, Typography, useTheme } from "@mui/material";
import { connect, ConnectedProps } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { thunkClearReceiptDetails } from "../../store/actions/CustomerCareActions";
import { IStore } from "../../store/IStore";
import { Api } from "../../api/Api";
import { CustomerInteractionDto, InteractionType, RewardedInteractionDto } from "../../store/models/customer/CustomerInteractionDto";
import { ReceiptDetailsDto } from "../../store/models/customer/ReceiptDetailsDto";
import { dialogStyles } from "../atoms/DialogStyle";
import ImsTable, { ImsHeadCell, ImsTableRow } from "../atoms/ImsEmbeddedTable";
import { COLOR_GRAY_1 } from "../atoms/ImsMaterialTheme";
import { formatEuro, formatGermanDate, formatGermanDateTime } from "../atoms/Utils";
import { formatRewards } from "./KeyFigures";
import NotificationList from "../notification/NotificationList";
import { useTranslation } from "react-i18next";

interface ReceiptDialogProps extends ThunkProps {
  interaction: CustomerInteractionDto;
  receiptDetails?: ReceiptDetailsDto;
  setReceiptDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReceiptDialog = (props: ReceiptDialogProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [rewardedInteractions, setRewardedInteractions] = useState<RewardedInteractionDto[]>([]);
  const [externalIdentificationNumber, setExternalIdentificationNumber] = useState<string | undefined>();

  useEffect(() => {
    Api.getInteractionDetails(props.interaction.interactionNumber)
      .then((details) => {
        if (details.rewardedInteractions) {
          setRewardedInteractions(details.rewardedInteractions);
        }
        setExternalIdentificationNumber(details.externalIdentificationNumber);
      })
      .catch((error) => console.error("Error fetching interaction details:", error));
  }, [props.interaction.interactionNumber]);

  const getOperationalUnitName = (externalUnitNumber?: string) =>
    props.operationalUnits.find((operationalUnit) => operationalUnit.externalUnitNumber === externalUnitNumber)
      ?.unitName;

  const bonusHeadCells: ImsHeadCell[] = [
    { title: t("receiptDialog.headingBonusRule"), align: "left" },
    { title: t("receiptDialog.headingValidFrom"), align: "left" },
    { title: "Belohnung", align: "right", headAlign: "right" },
  ];

  const bonusRows: ImsTableRow[] | undefined = rewardedInteractions.length > 0
    ? rewardedInteractions.map((row, index) => ({
        key: index,
        nodes: [
          <>{row.bonusReason}</>,
          <>{formatGermanDate(row.validFrom)}</>,
          formatRewards(row.bonusPoints, row.stampCard, row.rewardType)
        ],
      }))
    : undefined;

  const bonusSum = rewardedInteractions
    .filter((r) => r.rewardType !== "STAMP_CARD")
    .map((rewardedInteraction) => {
      const points = Number(rewardedInteraction.bonusPoints);
      return Number.isNaN(points) ? 0 : points;
    })
    .reduce((a, b) => a + b, 0);

  const stampSum = rewardedInteractions
    .filter((r) => r.rewardType === "STAMP_CARD")
    .map((rewardedInteraction) => {
      const value = Number(rewardedInteraction.bonusPoints || rewardedInteraction.stampCard || 0);
      return Number.isNaN(value) ? 0 : value;
    })
    .reduce((a, b) => a + b, 0);

  const headCells: ImsHeadCell[] = [
    { title: t("receiptDialog.headingArticleDescription"), align: "left" },
    { title: t("receiptDialog.headingQuantity"), align: "right" },
    { title: t("receiptDialog.headingPrice"), align: "right", headAlign: "right" },
  ];

  const rows: ImsTableRow[] | undefined = props.receiptDetails?.receiptDetails.map((row, index) => ({
    key: index,
    nodes: [
      row.itemInfoList
        .filter((itemInfo) => itemInfo.value)
        .map((itemInfo) => (
          <Grid container key={itemInfo.description}>
            <Grid item sm={7}>
              <Typography variant="subtitle1">{itemInfo.description}</Typography>
            </Grid>
            <Grid item sm={5}>
              {itemInfo.value}
            </Grid>
          </Grid>
        )),
      <>{Number(row.quantity)}</>,
      <>{formatEuro(row.salesPrice)}</>,
    ],
  }));

  return (
    <Dialog
      onClose={() => {
        props.thunkClearReceiptDetails();
        props.setReceiptDialogVisible(false);
      }}
      open={true}
      fullWidth={false}
      maxWidth="md"
      PaperProps={{
        sx: dialogStyles.scrollIn,
      }}
    >
      <NotificationList isDialog />
      <DialogTitle style={{ padding: theme.spacing(0) }}>
        <Grid container style={{ padding: theme.spacing(3) }}>
          {props.interaction?.interactionType === InteractionType.REVERSAL_INTERACTION ? (
            <b>{t("receiptDialog.labelReturnDetails")}</b>
          ) : (
            <b>{t("receiptDialog.labelPurchaseDetails")}</b>
          )}
        </Grid>

        <Divider
          style={{
            color: COLOR_GRAY_1,
            height: "2px",
          }}
          light={true}
        />

        <Grid container style={{ padding: theme.spacing(3) }}>
          <Grid item sm={6}>
            <Typography variant="subtitle1">{t("receiptDialog.labelDateTime")}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="body1">
              {props.interaction.interactionTimestamp &&
                formatGermanDateTime(new Date(props.interaction.interactionTimestamp))}
            </Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="subtitle1">{t("receiptDialog.labelStore")}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="body1">{getOperationalUnitName(props.interaction.externalUnitNumber)}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="subtitle1">{t("receiptDialog.labelReceiptId")}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="body1">{props.interaction.externalReceiptId}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="subtitle1">{t("receiptDialog.labelCardNumber")}</Typography>
          </Grid>
          <Grid item sm={6}>
            <Typography variant="body1">{externalIdentificationNumber}</Typography>
          </Grid>
          {!rows && (
            <Grid container>
              <Grid item sm={6}>
                <Typography variant="subtitle1">{t("receiptDialog.labelRevenue")}</Typography>
              </Grid>
              <Grid item sm={6}>
                {formatAmount(props.interaction)}
              </Grid>
              <p style={{ paddingLeft: theme.spacing(20) }}>{t("receiptDialog.noReceiptDataFound")}</p>
            </Grid>
          )}
          {rows && (
            <Divider
              style={{
                color: COLOR_GRAY_1,
                height: "2px",
              }}
              light={true}
            />
          )}
        </Grid>
      </DialogTitle>

      {props.interaction && bonusRows?.length ? (
        <>
          <DialogContent
            style={{
              padding: theme.spacing(0),
              overflow: "hidden",
              display: "table",
            }}
          >
            <ImsTable headCells={bonusHeadCells} rows={bonusRows} verticalAlign="top" compact />
          </DialogContent>
          {(bonusSum || stampSum) && (
            <Grid
              container
              justifyContent="flex-end"
              style={{
                padding: theme.spacing(2, 3),
              }}
            >
              <div>
                {bonusSum && (
                  <div>
                    <b>{t("receiptDialog.labelBonusSum")}: {bonusSum}</b>
                  </div>
                )}
                {stampSum && (
                  <div>
                    <b>Stempelkarten gesamt: {stampSum}</b>
                  </div>
                )}
              </div>
            </Grid>
          )}
          <Divider style={{ color: COLOR_GRAY_1, height: "2px" }} light={true} />
        </>
      ) : null}

      {rows && (
        <>
          <DialogContent
            style={{
              padding: theme.spacing(0),
              overflow: "hidden",
              display: "flex",
            }}
          >
            <ImsTable headCells={headCells} rows={rows} verticalAlign="top" />
          </DialogContent>
          <Divider style={{ color: COLOR_GRAY_1, height: "2px" }} light={true} />

          <Grid
            container
            justifyContent="flex-end"
            style={{
              padding: theme.spacing(2),
            }}
          >
            {props.interaction?.interactionType === InteractionType.REVERSAL_INTERACTION ? (
              <b>
                {t("receiptDialog.labelSum")}: {formatEuro(props.interaction.reversalAmount)}
              </b>
            ) : (
              <b>
                {t("receiptDialog.labelSum")}: {formatEuro(props.interaction.paymentAmount)}
              </b>
            )}
          </Grid>
        </>
      )}
    </Dialog>
  );
};

function formatAmount(interaction: CustomerInteractionDto) {
  const amount = Number(interaction.paymentAmount) || Number(interaction.reversalAmount) * -1;
  return formatEuro(amount);
}

const mapStateToProps = (state: IStore) => ({
  operationalUnits: state.operationalUnits.operationalUnits,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ thunkClearReceiptDetails }, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(ReceiptDialog);
